import { FC, useEffect, useState } from "react";
import { Autocomplete, TextField } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import ClearIcon from "@mui/icons-material/Clear";
import { useMutation } from "react-query";
import { fetchTaskCounterUserTemplates } from "../miscellaneous/fetchTaskCounterSubmission";
import { TaskCounterUserTemplate } from "../types/TaskCounterTypes";
import { TextInput } from "../../common/components/TextInput";
import { SearchButton } from "../../common/components/SearchButton";
import { ButtonCancelA } from "../../common/components/ButtonCancelA";
import { Controller, FieldValues, useForm } from "react-hook-form";
import { TaskCounterSubmissionCriteria } from "../miscellaneous/taskCounterSubmissionSearchAtom";

type Props = {
  setSearchParams: (params: any) => void;
  setPage: (page: number) => void;
  defaultValues?: TaskCounterSubmissionCriteria;
  onAtomReset?: () => void;
};

export const TaskCounterSubmissionSearch: FC<Props> = ({
  setSearchParams,
  setPage,
  defaultValues,
  onAtomReset,
}) => {
  const { reset, control, register, handleSubmit } = useForm<FieldValues>({
    defaultValues: {
      taskId: defaultValues?.taskId ? String(defaultValues.taskId) : "",
      taskName: defaultValues?.taskName ?? "",
    },
  });
  const [dateCompleted, setDateCompleted] = useState<Dayjs | null>(
    defaultValues?.workDate ? dayjs(defaultValues.workDate) : null
  );
  const [templateOptions, setTemplateOptions] = useState<TaskCounterUserTemplate[]>([]);

  const mutationTemplates = useMutation(fetchTaskCounterUserTemplates, {
    onSuccess: (res) => {
      if (res.status === 200) {
        setTemplateOptions(
          (res.data as TaskCounterUserTemplate[]).filter((t) => t.taskType === 2)
        );
      }
    },
  });

  useEffect(() => {
    mutationTemplates.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSubmit = (data: any) => {
    setPage(0);
    setSearchParams({
      taskId: data.taskId ? parseInt(data.taskId) : null,
      taskName: data.taskName ?? "",
      workDate: dateCompleted ? dayjs(dateCompleted).format() : null,
      timezone: dayjs().format(),
    });
  };

  const handleReset = () => {
    reset({ taskId: "", taskName: "" });
    setDateCompleted(null);
    setPage(0);
    setSearchParams({});
    onAtomReset?.();
  };

  return (
    <div className="mt-9 border-2 pt-7 pb-7">
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="flex flex-row justify-center">
          <div className="m-0 mx-5">
            <TextInput
              label={"Task ID"}
              value=""
              register={register("taskId")}
            />
          </div>

          <div className="m-0 mx-5">
            <TextInput
              label={"Task Name"}
              value=""
              register={register("taskName")}
            />
          </div>

          <div className="m-0 mx-5">
            <Controller
              name="dateCompleted"
              control={control}
              defaultValue={dateCompleted}
              render={({ field }) => (
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <div className="flex flex-row items-center">
                    <label className="font-bold mr-3">Date Completed</label>
                    <DatePicker
                      value={dateCompleted}
                      onChange={(newValue) => {
                        setDateCompleted(newValue as any);
                      }}
                      sx={{
                        "& .MuiInputBase-input": {
                          height: "5px",
                        },
                      }}
                    />
                  </div>
                </LocalizationProvider>
              )}
            />
          </div>
        </div>

        <div className="flex flex-center justify-center mt-10 gap-5">
          <ButtonCancelA
            name="Reset"
            onHandleClick={() => {
              handleReset();
            }}
            icon={<ClearIcon />}
          />
          <SearchButton />
        </div>
      </form>
    </div>
  );
};
